import { Task } from "@/bryntum-lib/models/Task";
import { TaskRenderer } from "@/modern/components/task-renderer";
import { Button, buttonVariants } from "@/modern/components/ui/button-shadcn";
import { Input } from "@/modern/components/ui/input";
import {
  HoverCard,
  HoverCardContent,
  HoverCardTrigger,
} from "@/modern/components/ui/hover-card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from "@/modern/components/ui/dialog";
import { cn } from "@/modern/utils";
import { uploadFile } from "@/network/network.jsx";
import { getIsArchivedForBoard } from "@/other/utils/otherUtils";
import BryntumContext from "@/react/context/bryntumContext.jsx";
import { getSavedBoardSelection } from "@/react/hooks/useBoards";
import { useSwivuForm } from "@/react/hooks/useSwivuForm.jsx";
import { Info, X } from "lucide-react";
import { format, isSameYear, isSameMonth, isSameDay } from "date-fns";
import { useCallback, useContext, useEffect, useMemo, useRef, useState, type RefObject } from "react";
import { Controller, type FieldErrors } from "react-hook-form";
import { toast } from "sonner";
import SpotIncentiveImagePreview from "./SpotIncentiveImagePreview";
import { LightboxImageTrigger } from "@/modern/components/ui/lightbox";
import SpotIncentivePointsAssignment from "./SpotIncentivePointsAssignment";
import SpotIncentiveResourceSelector from "./SpotIncentiveResourceSelector";
import { SpotIncentiveFormData, spotIncentiveSchema } from "./spotIncentiveSchema";
import SpotIncentiveTaskSelector from "./SpotIncentiveTaskSelector";
import { SPOT_INCENTIVE_DIALOG_Z } from "./SpotIncentivePickerShell";
import { useFileUrl } from "@/react/components/entityViews/files/api/files.api";
import { createFile } from "@/react/components/entityViews/files/api/files.service";
import type { SwivuFile } from "@/react/components/entityViews/files/types/swivuFile";
import type { SpotIncentiveFileData } from "@/react/components/entityViews/files/utils/spotIncentiveFileUtils";

interface SpotIncentiveProps {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  record: any;
  onClose: () => void;
  editorInstance?: unknown;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  selectedTask?: any;
  initialFileId?: number;
  initialFileData?: SpotIncentiveFileData;
  isCreatingUnreleasedRecord?: boolean;
  onReviewClick?: (newFile?: SwivuFile) => void;
  initialName?: string;
  initialStartDate?: string;
  initialEndDate?: string;
  initialResourceIds?: number[];
  layout?: "modal" | "embedded";
  /**
   * File Detail rail only — which surface is shown in the Adjust Points panel.
   * Controlled by AdjustPointsPanel so Back can pop picker → form → Details.
   */
  embeddedView?: SpotIncentiveEmbeddedView;
  onEmbeddedViewChange?: (view: SpotIncentiveEmbeddedView) => void;
}

export type SpotIncentiveEmbeddedView = "form" | "task" | "resources" | "points";

export const SPOT_INCENTIVE_EMBEDDED_TITLES: Record<SpotIncentiveEmbeddedView, string> = {
  form: "Adjust Points",
  task: "Select task",
  resources: "Select Resources",
  points: "Adjust Points",
};

const FIELD_LABEL =
  "mb-0 block text-sm font-normal uppercase tracking-wide text-muted-foreground";

/** Soft plane — tighter top so section headers sit closer to the card edge. */
const SOFT_BAND = "rounded-2xl border border-border/60 bg-muted/40 px-5 pb-4 pt-3";

const FIELD_STACK = "space-y-2";
const BAND_STACK = "space-y-3";

/** Reserved outline pills — same size across Task / Resources / Evidence / Points. */
const ACTION_PILL = "h-9 w-[4.75rem] shrink-0 rounded-full px-3 text-sm font-normal";
const ACTION_PILL_ERROR =
  "border-destructive text-destructive hover:bg-destructive/10 hover:text-destructive";
/** Row height comes from the pill; label centers without its own h-9 box adding top air. */
const SECTION_HEADER = "flex items-center justify-between gap-3";
const SECTION_LABEL =
  "mb-0 text-sm font-normal uppercase leading-none tracking-wide text-muted-foreground";
const FIELD_HINT = "text-sm font-normal leading-relaxed text-muted-foreground";
const FIELD_ERROR = "text-sm font-normal leading-relaxed text-destructive";
const EVIDENCE_UPLOAD_INPUT_ID = "spot-incentive-evidence-upload";
function FieldSupport({
  error,
  hint,
}: {
  error?: string | null;
  hint?: string | null;
}) {
  if (error) {
    return (
      <p className={FIELD_ERROR} role="alert">
        {error}
      </p>
    );
  }
  if (hint) return <p className={FIELD_HINT}>{hint}</p>;
  return null;
}

function formatHumanDateRange(startIso: string, endIso: string): string {
  const start = new Date(startIso);
  const end = new Date(endIso);
  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return "";
  if (isSameDay(start, end)) return format(start, "MMM d, yyyy");
  if (isSameMonth(start, end) && isSameYear(start, end)) {
    return `${format(start, "MMM d")} – ${format(end, "d, yyyy")}`;
  }
  if (isSameYear(start, end)) {
    return `${format(start, "MMM d")} – ${format(end, "MMM d, yyyy")}`;
  }
  return `${format(start, "MMM d, yyyy")} – ${format(end, "MMM d, yyyy")}`;
}

function joinHumanList(parts: string[]): string {
  if (parts.length === 0) return "";
  if (parts.length === 1) return parts[0];
  if (parts.length === 2) return `${parts[0]} and ${parts[1]}`;
  return `${parts.slice(0, -1).join(", ")}, and ${parts[parts.length - 1]}`;
}

type PointsTagRef = { name: string; points: number };

function groupTagsByPoints(tags: PointsTagRef[]): Array<{ points: number; names: string[] }> {
  const byPoints = new Map<number, string[]>();
  tags.forEach((tag) => {
    const names = byPoints.get(tag.points) ?? [];
    names.push(tag.name);
    byPoints.set(tag.points, names);
  });
  return Array.from(byPoints.entries()).map(([points, names]) => ({ points, names }));
}

const RESOURCE_NAME_PREVIEW = 2;

function ResourceNamesSummary({ names }: { names: string[] }) {
  if (names.length === 0) return null;
  if (names.length === 1) {
    return <span className="text-base font-normal leading-snug text-foreground/90">{names[0]}</span>;
  }
  if (names.length === 2) {
    return (
      <span className="text-base font-normal leading-snug text-foreground/90">
        {names[0]} and {names[1]}
      </span>
    );
  }

  const visible = names.slice(0, RESOURCE_NAME_PREVIEW);
  const remaining = names.length - RESOURCE_NAME_PREVIEW;

  return (
    <span className="inline text-base font-normal leading-snug text-foreground/90">
      {visible.join(", ")}, and{" "}
      <HoverCard openDelay={120} closeDelay={80}>
        <HoverCardTrigger asChild>
          <button
            type="button"
            className="relative top-[-1px] inline-flex items-center rounded-full border border-border/50 bg-transparent px-2 py-0.5 text-xs font-normal text-muted-foreground transition-colors hover:border-border hover:bg-muted/30 hover:text-foreground"
          >
            +{remaining} more
          </button>
        </HoverCardTrigger>
        <HoverCardContent
          align="start"
          side="top"
          sideOffset={8}
          className={cn(
            SPOT_INCENTIVE_DIALOG_Z,
            "w-56 rounded-lg border-border/60 p-0 shadow-xl",
          )}
        >
          <div className="border-b border-border/40 px-3 py-2">
            <p className="text-xs font-normal uppercase tracking-wide text-muted-foreground">
              All resources
            </p>
          </div>
          <ul className="max-h-48 space-y-0 overflow-y-auto p-1.5">
            {names.map((name, index) => (
              <li
                key={`${name}-${index}`}
                className="rounded-md px-2 py-1.5 text-sm font-normal text-foreground"
              >
                {name}
              </li>
            ))}
          </ul>
        </HoverCardContent>
      </HoverCard>
    </span>
  );
}

function PointsBreakdownSummary({
  positive,
  negative,
}: {
  positive: PointsTagRef[];
  negative: PointsTagRef[];
}) {
  const awardGroups = groupTagsByPoints(positive);
  const deductGroups = groupTagsByPoints(negative);

  const awardNodes = awardGroups.map((group, index) => (
    <span key={`award-${group.points}-${index}`}>
      {index > 0 ? " and " : null}
      <span className="tabular-nums text-success">+{group.points}</span>
      {" for "}
      {joinHumanList(group.names)}
    </span>
  ));

  const deductNodes = deductGroups.map((group, index) => (
    <span key={`deduct-${group.points}-${index}`}>
      {index > 0 ? " and " : null}
      <span className="tabular-nums text-destructive">−{group.points}</span>
      {" for "}
      {joinHumanList(group.names)}
    </span>
  ));

  return (
    <p className="text-sm font-normal leading-relaxed text-muted-foreground">
      {awardNodes.length > 0 ? (
        <>
          Awarding {awardNodes}
          {deductNodes.length > 0 ? ", and deducting " : "."}
        </>
      ) : null}
      {deductNodes.length > 0 ? (
        <>
          {awardNodes.length === 0 ? "Deducting " : null}
          {deductNodes}.
        </>
      ) : null}
    </p>
  );
}

const SpotIncentive = ({
  record,
  onClose,
  editorInstance,
  selectedTask,
  initialFileId,
  initialFileData,
  isCreatingUnreleasedRecord,
  onReviewClick,
  initialName,
  initialStartDate,
  initialEndDate,
  initialResourceIds,
  layout = "modal",
  embeddedView: embeddedViewProp = "form",
  onEmbeddedViewChange,
}: SpotIncentiveProps) => {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const { project } = useContext(BryntumContext) as any;

  const initialDocumentId = initialFileData?.documentId || null;
  const initialFileName = initialFileData?.fileName || undefined;

  const { form, formState } = useSwivuForm({
    schema: spotIncentiveSchema,
    record,
    updateRecordOnFormValueChange: false,
    version: "",
    defaultValues: {
      name: initialName || "",
      taskId: selectedTask?.id || null,
      resourceIds: initialResourceIds || [],
      startDate:
        initialStartDate ||
        (() => {
          const date = new Date();
          date.setDate(date.getDate() - 5);
          return date.toISOString();
        })(),
      endDate: initialEndDate || new Date().toISOString(),
      positiveTagIds: [],
      negativeTagIds: [],
      multiplyByDays: false,
      fileId: initialFileId || null,
      uploadedDocumentId: initialDocumentId,
      uploadedFileName: initialFileName,
    },
  });

  const [showTaskSelector, setShowTaskSelector] = useState(false);
  const [showResourceSelector, setShowResourceSelector] = useState(false);
  const [showPointsAssignment, setShowPointsAssignment] = useState(false);
  const [showInactiveDialog, setShowInactiveDialog] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);
  const uploadInputRef = useRef<HTMLInputElement>(null);
  const [createdSpotIncentiveId, setCreatedSpotIncentiveId] = useState<number | null>(null);
  const uploadedFileRecord = useRef<SwivuFile | undefined>(undefined);

  const isEmbedded = layout === "embedded";
  const embeddedView = isEmbedded ? embeddedViewProp : "form";
  const setEmbeddedView = useCallback(
    (view: SpotIncentiveEmbeddedView) => {
      onEmbeddedViewChange?.(view);
    },
    [onEmbeddedViewChange],
  );

  const openTaskPicker = useCallback(() => {
    if (isEmbedded) setEmbeddedView("task");
    else setShowTaskSelector(true);
  }, [isEmbedded, setEmbeddedView]);

  const openResourcePicker = useCallback(() => {
    if (isEmbedded) setEmbeddedView("resources");
    else setShowResourceSelector(true);
  }, [isEmbedded, setEmbeddedView]);

  const openPointsPicker = useCallback(() => {
    if (isEmbedded) setEmbeddedView("points");
    else setShowPointsAssignment(true);
  }, [isEmbedded, setEmbeddedView]);

  const closeEmbeddedPicker = useCallback(() => {
    setEmbeddedView("form");
  }, [setEmbeddedView]);

  const pointsSectionRef = useRef<HTMLDivElement>(null);
  const nameSectionRef = useRef<HTMLDivElement>(null);
  const taskSectionRef = useRef<HTMLDivElement>(null);
  const resourceSectionRef = useRef<HTMLDivElement>(null);

  const uploadedFileUrl = useMemo(() => {
    if (uploadedFile) return URL.createObjectURL(uploadedFile);
    return null;
  }, [uploadedFile]);

  useEffect(() => {
    return () => {
      if (uploadedFileUrl) URL.revokeObjectURL(uploadedFileUrl);
    };
  }, [uploadedFileUrl]);

  const tasks = useMemo(
    () =>
      project?.extraTaskStore?.query((taskRecord: Task) => {
        const selectedBoard = getSavedBoardSelection();
        const canShowArchivedRecords = getIsArchivedForBoard(selectedBoard.slug);
        return !taskRecord.isPhantom && (canShowArchivedRecords || !taskRecord.archived);
      }, true) || [],
    [project],
  );

  const getSelectedTaskRecord = (id: number | null): Task | null => {
    if (!id) return null;
    return (tasks.find((t: Task) => t.id === id) as Task | undefined) ?? null;
  };

  const getSelectedResourceNames = (ids: number[]) => {
    if (ids.length === 0) return [];
    if (!project?.resourceStore) return ids.map((id) => `Resource ${id}`);
    return ids.map((id) => {
      const resource = project.resourceStore.getById(id);
      return resource?.name || `Resource ${id}`;
    });
  };

  const getTotalPoints = (pos: number[], neg: number[]): number => {
    let total = 0;
    pos.forEach((tagId) => {
      const tag = project?.incentiveTagsStore?.getById(tagId);
      if (tag?.categoryRecord) total += tag.categoryRecord.points;
    });
    neg.forEach((tagId) => {
      const tag = project?.incentiveTagsStore?.getById(tagId);
      if (tag?.categoryRecord) total -= tag.categoryRecord.points;
    });
    return total;
  };

  const taskId = form.watch("taskId");
  const resourceIds = form.watch("resourceIds");
  const positiveTagIds = form.watch("positiveTagIds");
  const negativeTagIds = form.watch("negativeTagIds");
  const startDate = form.watch("startDate");
  const endDate = form.watch("endDate");
  const multiplyByDays = form.watch("multiplyByDays");
  const uploadedDocumentId = form.watch("uploadedDocumentId");
  const uploadedFileName = form.watch("uploadedFileName");
  const fileId = form.watch("fileId");
  const { url: existingFileUrl } = useFileUrl(fileId ?? null);

  const hasPointsAssigned = positiveTagIds.length > 0 || negativeTagIds.length > 0;
  const isResourceSelectionLocked = !taskId;
  const isPointsAssignmentLocked = !taskId || resourceIds.length === 0;
  const totalPoints = getTotalPoints(positiveTagIds, negativeTagIds);
  const humanDateRange =
    startDate && endDate ? formatHumanDateRange(startDate, endDate) : "";
  const resourceNames = getSelectedResourceNames(resourceIds);
  const shouldShowNoResourcesWarning =
    isCreatingUnreleasedRecord && resourceIds.length === 0 && Boolean(taskId);
  const effectiveDocumentId = typeof uploadedDocumentId === "number" ? uploadedDocumentId : undefined;
  const selectedTaskRecord = getSelectedTaskRecord(taskId);

  const handleFileUpload = useCallback(
    async (file: File) => {
      if (!file.type.startsWith("image/")) {
        toast.error("Only image files are allowed");
        return;
      }
      setIsUploading(true);
      try {
        const { data } = await uploadFile(file, file.name);
        form.setValue("uploadedDocumentId", data.id);
        form.setValue("uploadedFileName", file.name);
        setUploadedFile(file);
        toast.success("Image uploaded successfully");
      } catch (error) {
        console.error("File upload error:", error);
        toast.error("Upload failed. Please try again.");
      } finally {
        setIsUploading(false);
      }
    },
    [form],
  );

  useEffect(() => {
    if (initialFileData?.file) {
      handleFileUpload(initialFileData.file);
    }
  }, [initialFileData, handleFileUpload]);

  const showSuccessToast = () => {
    toast.success("Points adjusted are now visible on the Points surface.");
  };

  const handleSave = async (data: SpotIncentiveFormData) => {
    setIsSubmitting(true);
    try {
      const selectedBoard = getSavedBoardSelection();
      let finalFileId = data.fileId;

      if (data.uploadedDocumentId && !finalFileId) {
        const newFile = await createFile({
          document: data.uploadedDocumentId,
          board_id: selectedBoard.id,
          task: data.taskId ?? undefined,
          name: data.uploadedFileName,
        });
        finalFileId = newFile.id;
        uploadedFileRecord.current = newFile;
      }

      const [newSpotIncentive] = project.spotIncentiveStore.add({
        name: data.name,
        taskId: data.taskId,
        resourceIds: data.resourceIds,
        positiveTagIds: data.positiveTagIds,
        negativeTagIds: data.negativeTagIds,
        startDate: data.startDate,
        endDate: data.endDate,
        boardId: selectedBoard.id,
        multiplyByDays: data.multiplyByDays,
        fileId: finalFileId || null,
        isReleased: !isCreatingUnreleasedRecord,
      });

      if (isCreatingUnreleasedRecord) {
        await project.sync();
        setCreatedSpotIncentiveId(newSpotIncentive.id);
        setShowInactiveDialog(true);
      } else {
        showSuccessToast();
        onClose();
      }
    } catch {
      toast.error("Failed to create spot point", {
        description: "Please try again or contact support if the issue persists.",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const scrollToFirstError = (errors: FieldErrors<SpotIncentiveFormData>) => {
    const order: Array<{ key: keyof SpotIncentiveFormData; ref: RefObject<HTMLDivElement | null> }> = [
      { key: "name", ref: nameSectionRef },
      { key: "taskId", ref: taskSectionRef },
      { key: "resourceIds", ref: resourceSectionRef },
      { key: "positiveTagIds", ref: pointsSectionRef },
    ];
    for (const { key, ref } of order) {
      if (errors[key]) {
        ref.current?.scrollIntoView({ behavior: "smooth", block: "center" });
        break;
      }
    }
  };

  const primaryLabel = isSubmitting
    ? "Creating..."
    : isCreatingUnreleasedRecord
      ? "Save"
      : "Adjust Points";

  const saveButton = (
    <Button
      type="button"
      disabled={isSubmitting}
      onClick={form.handleSubmit(handleSave, scrollToFirstError)}
      className={cn("h-9 rounded-full px-3 text-sm font-normal", isEmbedded && "w-full")}
    >
      {primaryLabel}
    </Button>
  );

  const pointsError = formState.errors.positiveTagIds && !hasPointsAssigned;
  const nameError = formState.errors.name;
  const taskError = formState.errors.taskId;
  const resourceError = formState.errors.resourceIds;

  const hasEvidence = Boolean(
    (fileId && existingFileUrl) || (uploadedFile && uploadedFileUrl) || uploadedDocumentId,
  );

  /** Native label wraps the file input — works inside Radix Dialog (no inert / display:none click issues). */
  const evidenceActionButton = (
    <label
      className={cn(
        buttonVariants({ variant: "outline" }),
        ACTION_PILL,
        "cursor-pointer",
        isUploading && "pointer-events-none opacity-50",
      )}
    >
      {isUploading ? "Upload" : hasEvidence ? "Change" : "Upload"}
      <input
        id={EVIDENCE_UPLOAD_INPUT_ID}
        ref={uploadInputRef}
        type="file"
        accept="image/*"
        disabled={isUploading}
        className="sr-only"
        tabIndex={-1}
        onChange={(e) => {
          const file = e.target.files?.[0];
          if (file) handleFileUpload(file);
          e.target.value = "";
        }}
      />
    </label>
  );

  const evidencePreview = (src: string, alt: string) => (
    <SpotIncentiveImagePreview images={[{ src, alt }]} allowZoom enablePreview>
      <LightboxImageTrigger index={0} asChild>
        <button
          type="button"
          className="flex min-w-0 max-w-full cursor-pointer items-center gap-3 rounded-lg text-left transition-colors hover:opacity-90"
        >
          <img
            src={src}
            alt=""
            className="h-10 w-10 shrink-0 rounded-md object-cover"
          />
          <div className="min-w-0">
            <p className="truncate text-base font-normal leading-snug text-foreground">{alt}</p>
            <p className="mt-0.5 text-sm text-muted-foreground">Tap to view</p>
          </div>
        </button>
      </LightboxImageTrigger>
    </SpotIncentiveImagePreview>
  );

  const evidenceBody = (
    <div className="min-w-0">
      {fileId && existingFileUrl ? (
        evidencePreview(existingFileUrl, uploadedFileName || "Evidence photo")
      ) : uploadedFile && uploadedFileUrl ? (
        evidencePreview(uploadedFileUrl, uploadedFileName || "Evidence photo")
      ) : uploadedDocumentId ? (
        <p className="truncate text-base font-normal leading-snug text-foreground">
          {uploadedFileName || "Evidence photo"}
        </p>
      ) : (
        <p className="text-base font-normal leading-snug text-muted-foreground">
          {isUploading
            ? "Uploading…"
            : "Adding a photo is optional but helps AI suggest points."}
        </p>
      )}
    </div>
  );

  const nameErrorMessage = nameError ? String(formState.errors.name?.message) : null;
  const taskErrorMessage = taskError ? String(formState.errors.taskId?.message) : null;
  const resourceErrorMessage = resourceError
    ? String(formState.errors.resourceIds?.message)
    : null;
  const pointsErrorMessage = pointsError
    ? String(formState.errors.positiveTagIds?.message)
    : null;

  const formFields = (
    <div className="space-y-4">
      {/* Name — first */}
      <div ref={nameSectionRef} className={cn(SOFT_BAND, BAND_STACK)}>
        <label
          htmlFor="incentive-name"
          className={cn(FIELD_LABEL, nameError && "text-destructive")}
        >
          Name
        </label>
        <Controller
          control={form.control}
          name="name"
          render={({ field }) => (
            <Input
              id="incentive-name"
              autoComplete="off"
              placeholder="e.g. Punch list bonus"
              aria-invalid={Boolean(nameError)}
              className={cn(
                "h-11 rounded-xl border-input bg-background text-sm shadow-none",
                "focus-visible:ring-0 focus-visible:ring-offset-0",
                nameError
                  ? "border-destructive focus-visible:border-destructive"
                  : "focus-visible:border-ring",
              )}
              {...field}
            />
          )}
        />
        <FieldSupport
          error={nameErrorMessage}
          hint="A short label so your team can recognize this adjustment later."
        />
      </div>

      {/* Context — Task + Resources */}
      <div className={cn(SOFT_BAND, "space-y-5")}>
        <div
          ref={taskSectionRef}
          className={isEmbedded ? BAND_STACK : FIELD_STACK}
        >
          {isEmbedded ? (
            <label className={cn(FIELD_LABEL, taskError && "text-destructive")}>
              Task
            </label>
          ) : (
            <div className={SECTION_HEADER}>
              <label className={cn(SECTION_LABEL, taskError && "text-destructive")}>
                Task
              </label>
              <Button
                type="button"
                variant="outline"
                disabled={isCreatingUnreleasedRecord}
                onClick={openTaskPicker}
                className={cn(ACTION_PILL, taskError && ACTION_PILL_ERROR)}
              >
                {taskId ? "Change" : "Select"}
              </Button>
            </div>
          )}
          <div className="min-w-0">
            {selectedTaskRecord ? (
              <TaskRenderer record={selectedTaskRecord} />
            ) : (
              <FieldSupport error={taskErrorMessage} hint="Choose a task…" />
            )}
          </div>
          {selectedTaskRecord ? <FieldSupport error={taskErrorMessage} /> : null}
        </div>

        <div className="h-px bg-border/40" aria-hidden />

        <div ref={resourceSectionRef} className={FIELD_STACK}>
          <div className={SECTION_HEADER}>
            <label
              className={cn(
                SECTION_LABEL,
                resourceError && !isResourceSelectionLocked && "text-destructive",
              )}
            >
              Resources
            </label>
            <Button
              type="button"
              variant="outline"
              disabled={isResourceSelectionLocked}
              onClick={openResourcePicker}
              className={cn(
                ACTION_PILL,
                resourceError && !isResourceSelectionLocked && ACTION_PILL_ERROR,
              )}
            >
              {resourceIds.length > 0 ? "Edit" : "Select"}
            </Button>
          </div>
          {isResourceSelectionLocked ? (
            <FieldSupport hint="Choose a task first." />
          ) : resourceIds.length > 0 ? (
            <div className="min-w-0 space-y-2">
              <ResourceNamesSummary names={resourceNames} />
              <FieldSupport
                error={resourceErrorMessage}
                hint={
                  humanDateRange
                    ? `${
                        resourceIds.length === 1
                          ? "The user eligible for this resource"
                          : "The users eligible for these resources"
                      } will receive points for performance on this task during ${humanDateRange}${
                        multiplyByDays ? ", and points will be multiplied by days worked" : ""
                      }.`
                    : null
                }
              />
            </div>
          ) : (
            <FieldSupport
              error={resourceErrorMessage}
              hint="Select who receives points…"
            />
          )}

          {shouldShowNoResourcesWarning && !resourceError ? (
            <p className={FIELD_HINT}>
              No one was found on the photo date
              {startDate ? ` (${format(new Date(startDate), "MMM d, yyyy")})` : ""}.
            </p>
          ) : null}
        </div>
      </div>

      {/* Evidence — standalone only; File Detail already shows the photo */}
      {!isEmbedded ? (
        <div className={cn(SOFT_BAND, FIELD_STACK)}>
          <div className={SECTION_HEADER}>
            <p className={SECTION_LABEL}>Evidence</p>
            {evidenceActionButton}
          </div>
          {evidenceBody}
        </div>
      ) : null}

      {/* Points */}
      <div
        ref={pointsSectionRef}
        className={cn(SOFT_BAND, BAND_STACK, pointsError && "border-destructive")}
      >
        <div className={SECTION_HEADER}>
          <p className={cn(SECTION_LABEL, pointsError && "text-destructive")}>Points</p>
          <Button
            type="button"
            variant="outline"
            disabled={isPointsAssignmentLocked}
            onClick={() => {
              if (!isPointsAssignmentLocked) openPointsPicker();
            }}
            className={cn(ACTION_PILL, pointsError && ACTION_PILL_ERROR)}
          >
            {hasPointsAssigned ? "Edit" : "Adjust"}
          </Button>
        </div>

        <p
          className={cn(
            "text-2xl font-normal tracking-tight tabular-nums",
            totalPoints < 0
              ? "text-destructive"
              : totalPoints > 0
                ? "text-success"
                : "text-foreground",
          )}
        >
          {totalPoints > 0 ? "+" : ""}
          {totalPoints}
          <span className="ml-1.5 text-sm font-normal text-muted-foreground">points</span>
        </p>

        {hasPointsAssigned ? (
          <>
            <PointsBreakdownSummary
              positive={positiveTagIds.map((tagId: number) => {
                const tag = project?.incentiveTagsStore?.getById(tagId);
                return {
                  name: tag?.name || `Tag ${tagId}`,
                  points: tag?.categoryRecord?.points || 0,
                };
              })}
              negative={negativeTagIds.map((tagId: number) => {
                const tag = project?.incentiveTagsStore?.getById(tagId);
                return {
                  name: tag?.name || `Tag ${tagId}`,
                  points: tag?.categoryRecord?.points || 0,
                };
              })}
            />
            <FieldSupport error={pointsErrorMessage} />
          </>
        ) : (
          <FieldSupport
            error={pointsErrorMessage}
            hint={
              isPointsAssignmentLocked
                ? "Choose a task and resources before assigning points."
                : "No tags assigned yet."
            }
          />
        )}
      </div>
    </div>
  );

  const formChrome = isEmbedded ? (
    <div className="flex h-full min-h-0 flex-col">
      {embeddedView === "form" ? (
        <>
          <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-0.5 pr-1 pb-4">
            {formFields}
          </div>
          <div className="shrink-0 border-t border-border/40 pt-4">{saveButton}</div>
        </>
      ) : (
        <div className="flex h-full min-h-0 flex-1 flex-col">
          {embeddedView === "task" ? (
            <SpotIncentiveTaskSelector
              open
              presentation="inline"
              onOpenChange={(open) => {
                if (!open) closeEmbeddedPicker();
              }}
              selectedTaskId={taskId}
              onTaskSelect={(nextTaskId) => {
                form.setValue("taskId", nextTaskId);
                form.setValue("resourceIds", []);
                form.setValue("fileId", null);
                form.setValue("uploadedDocumentId", null);
                form.setValue("uploadedFileName", undefined);
                setUploadedFile(null);
              }}
              editorInstance={editorInstance}
            />
          ) : null}
          {embeddedView === "resources" ? (
            <SpotIncentiveResourceSelector
              open
              presentation="inline"
              onOpenChange={(open) => {
                if (!open) closeEmbeddedPicker();
              }}
              selectedResourceIds={resourceIds}
              onResourcesSelect={(data) => {
                form.setValue("resourceIds", data.resourceIds);
                form.setValue("startDate", data.startDate);
                form.setValue("endDate", data.endDate);
                form.setValue("multiplyByDays", data.multiplyByDays);
              }}
              selectedTaskId={taskId}
              editorInstance={editorInstance}
              multiplyByDays={multiplyByDays}
            />
          ) : null}
          {embeddedView === "points" ? (
            <SpotIncentivePointsAssignment
              open
              presentation="inline"
              onOpenChange={(open) => {
                if (!open) closeEmbeddedPicker();
              }}
              pointsAssignment={{ positiveTagIds, negativeTagIds }}
              onPointsChange={(points) => {
                form.setValue("positiveTagIds", points.positiveTagIds, { shouldValidate: true });
                form.setValue("negativeTagIds", points.negativeTagIds, { shouldValidate: true });
              }}
              documentId={effectiveDocumentId}
              editorInstance={editorInstance}
            />
          ) : null}
        </div>
      )}
    </div>
  ) : (
    <Dialog open onOpenChange={(next) => !next && onClose()}>
      <DialogContent
        showCloseButton={false}
        onPointerDownOutside={(e) => e.preventDefault()}
        onInteractOutside={(e) => e.preventDefault()}
        dialogOverlayClassName={SPOT_INCENTIVE_DIALOG_Z}
        className={cn(
          SPOT_INCENTIVE_DIALOG_Z,
          "flex max-h-[min(90vh,47rem)] w-[min(48rem,92vw)] max-w-[48rem] flex-col gap-0 overflow-hidden",
          "border-border/60 bg-background p-0 shadow-2xl sm:rounded-[2rem]",
        )}
      >
        <DialogTitle className="sr-only">Adjust Points</DialogTitle>
        <DialogDescription className="sr-only">
          Award or deduct points for resources against tasks
        </DialogDescription>

        <div className="flex shrink-0 items-start gap-3 px-5 pt-5 sm:px-7">
          <div className="min-w-0 flex-1">
            <h2 className="text-xl font-normal tracking-tight text-foreground sm:text-2xl">
              Adjust Points
            </h2>
            <p className="mt-0.5 text-sm font-normal text-muted-foreground">
              Award or deduct points for resources on a task
            </p>
          </div>
          <Button
            type="button"
            variant="outline"
            size="icon"
            disabled={isSubmitting}
            onClick={onClose}
            aria-label="Close"
            className="size-9 shrink-0 rounded-full border-input bg-muted text-foreground/80 shadow-sm hover:border-input hover:text-foreground"
          >
            <X className="size-4" />
          </Button>
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto px-5 pt-5 sm:px-7">
          <div className="px-0.5 pb-1">{formFields}</div>
        </div>

        <div className="flex shrink-0 items-center justify-end gap-2 px-5 pb-6 pt-4 sm:px-7">
          <Button
            type="button"
            variant="outline"
            disabled={isSubmitting}
            onClick={onClose}
            className="h-9 rounded-full px-3 text-sm font-normal"
          >
            Cancel
          </Button>
          {saveButton}
        </div>
      </DialogContent>
    </Dialog>
  );

  return (
    <>
      {formChrome}

      {!isEmbedded ? (
        <>
          <SpotIncentiveTaskSelector
            open={showTaskSelector}
            onOpenChange={setShowTaskSelector}
            selectedTaskId={taskId}
            onTaskSelect={(nextTaskId) => {
              form.setValue("taskId", nextTaskId);
              form.setValue("resourceIds", []);
              form.setValue("fileId", null);
              form.setValue("uploadedDocumentId", null);
              form.setValue("uploadedFileName", undefined);
              setUploadedFile(null);
            }}
            editorInstance={editorInstance}
          />

          <SpotIncentiveResourceSelector
            open={showResourceSelector}
            onOpenChange={setShowResourceSelector}
            selectedResourceIds={resourceIds}
            onResourcesSelect={(data) => {
              form.setValue("resourceIds", data.resourceIds);
              form.setValue("startDate", data.startDate);
              form.setValue("endDate", data.endDate);
              form.setValue("multiplyByDays", data.multiplyByDays);
            }}
            selectedTaskId={taskId}
            editorInstance={editorInstance}
            multiplyByDays={multiplyByDays}
          />

          <SpotIncentivePointsAssignment
            open={showPointsAssignment}
            onOpenChange={setShowPointsAssignment}
            pointsAssignment={{ positiveTagIds, negativeTagIds }}
            onPointsChange={(points) => {
              form.setValue("positiveTagIds", points.positiveTagIds, { shouldValidate: true });
              form.setValue("negativeTagIds", points.negativeTagIds, { shouldValidate: true });
            }}
            documentId={effectiveDocumentId}
            editorInstance={editorInstance}
          />
        </>
      ) : null}

      <Dialog open={showInactiveDialog} onOpenChange={setShowInactiveDialog}>
        <DialogContent
          showCloseButton={false}
          onInteractOutside={(e) => e.preventDefault()}
          dialogOverlayClassName={SPOT_INCENTIVE_DIALOG_Z}
          className={cn(
            SPOT_INCENTIVE_DIALOG_Z,
            "gap-0 overflow-hidden border-border/60 bg-background p-0 shadow-2xl sm:max-w-lg sm:rounded-[2rem]",
          )}
        >
          <DialogTitle className="sr-only">Pending Points Alert</DialogTitle>
          <DialogDescription className="sr-only">
            Release pending points or review the file
          </DialogDescription>
          <div className="space-y-3 px-5 pt-5 sm:px-7">
            <div className="flex items-center gap-2">
              <Info className="size-5 text-primary" />
              <h2 className="text-xl font-normal tracking-tight text-foreground">Pending points</h2>
            </div>
            <p className="text-sm leading-relaxed text-muted-foreground">
              To release pending points to your board users, choose Release Points below.
            </p>
          </div>
          <div className="flex flex-col-reverse gap-2 px-5 pb-6 pt-5 sm:flex-row sm:justify-end sm:px-7">
            <Button
              type="button"
              variant="outline"
              className="h-9 rounded-full px-3 text-sm font-normal"
              onClick={async () => {
                setShowInactiveDialog(false);
                if (project?.spotIncentiveStore && createdSpotIncentiveId) {
                  const spotIncentive = project.spotIncentiveStore.getById(createdSpotIncentiveId);
                  if (spotIncentive) {
                    spotIncentive.isReleased = true;
                    showSuccessToast();
                  }
                }
                onClose();
              }}
            >
              Release Points
            </Button>
            <Button
              type="button"
              className="h-9 rounded-full px-3 text-sm font-normal"
              onClick={() => {
                setShowInactiveDialog(false);
                if (onReviewClick) onReviewClick(uploadedFileRecord.current);
                onClose();
              }}
            >
              Review
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default SpotIncentive;
